import React from 'react';

import { NotificationBadge } from 'src/components/NotificationBadge';
import { FlagsContext } from 'src/context';
import { useNotificationBadgeData } from 'src/hooks/useNotificationBadgeData';
import { useAppSelector } from 'src/hooks/useStore';
import { useGetInboxNotificationsQuery } from 'src/services/api/inboxApi';

/**
 * AppNotificationBadge component
 * This component renders the notification badge for an installed app/extension/embed.
 * When the notifications api is used to create a notification with an api key
 * associated with an appId, that notification will be displayed with the associated app.
 */
export const AppNotificationBadge = ({ appId }: { appId: string }) => {
  const isClient = useAppSelector((state) => state.user.isClient);
  const activeCompanyId = useAppSelector(
    (state) => state.activeCompany.activeCompanyId,
  );
  const { EnableManyCompanies } = React.useContext(FlagsContext);
  const { backgroundColor, textColor } = useNotificationBadgeData(true); // isSidebar = true

  // ignore the query if the user is not a client, because the
  // app notification badge is only relevant to clients. IUs see
  // their notifications in the inbox.
  const { data: notifications } = useGetInboxNotificationsQuery(undefined, {
    skip: !isClient,
  });

  if (!isClient) {
    // similar to comment above, IUs see notifications
    // in in inbox so AppNotificationBadge should not
    // be rendered for them.
    return null;
  }

  // filter notifications by appId
  const appNotificationCount =
    notifications?.filter((notification) => {
      if (EnableManyCompanies && notification.companyId !== activeCompanyId) {
        return false;
      }

      if (
        notification.event === 'customApp.action' &&
        notification.appId === appId &&
        !notification.isRead
      ) {
        return true;
      }

      return false;
    }).length || 0;

  return (
    <NotificationBadge
      showZero={false}
      isSidebar
      content={appNotificationCount}
      backgroundColor={backgroundColor}
      textColor={textColor}
    />
  );
};
